import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Bell, Check, Send, RefreshCw, Trash2 } from 'lucide-react';
import { useToast } from '../Toast';
import { useTheme } from '../Theme';
import {
  fetchFeed, markRead, deleteNotification, clearNotifications, getPreferences, savePreferences,
  getCatalog, setTypeEnabled, broadcast, flushDigestsNow, CHANNEL_LABELS,
} from '../notifications';
import { setAppBadge } from '../badge';
import { parseAppPath } from '../navPath';

const BRAND = '#F05A28';
const ROLES = ['admin','bar_manager','bartender','barista','coffee_manager','production','sales','hr','kitchen_manager','cook','distilling'];

function relTime(ts) {
  const d = new Date(ts), s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}
const hourLabel = (h) => {
  if (h == null || h === '') return '';
  const ampm = h < 12 ? 'AM' : 'PM'; const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${ampm}`;
};

export default function Notifications({ user, onBack, onNavigate }) {
  const toast = useToast();
  const { theme } = useTheme();
  const dark = theme !== 'light';
  const isAdmin = (user.roles || []).includes('admin');

  const T = {
    card:    dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    bg:      dark ? 'bg-gray-800' : 'bg-white',
    title:   dark ? 'text-cream' : 'text-gray-900',
    text:    dark ? 'text-white' : 'text-gray-900',
    sub:     dark ? 'text-gray-400' : 'text-gray-600',
    input:   dark ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900 border border-gray-300',
    chip:    dark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700',
    divider: dark ? 'border-gray-700' : 'border-gray-200',
    row:     dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50',
  };
  const tabBtn = (active) =>
    `px-3.5 py-2 rounded-lg text-sm font-medium transition ${active ? 'text-white' : `${T.chip} hover:opacity-80`}`;

  const [tab, setTab] = useState('inbox');

  return (
    <div className={`min-h-screen ${dark ? 'bg-gray-900' : 'bg-gray-100'} px-4 sm:px-6 py-8 sm:py-10`}>
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className={`flex items-center gap-1.5 text-sm mb-6 ${T.sub} hover:opacity-80`}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="flex items-center gap-2.5 mb-6">
          <Bell size={26} style={{ color: BRAND }} />
          <h2 className={`text-3xl sm:text-4xl font-bold ${T.title}`}>Notifications</h2>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setTab('inbox')}    className={tabBtn(tab === 'inbox')}    style={tab === 'inbox' ? { backgroundColor: BRAND } : {}}>Inbox</button>
          <button onClick={() => setTab('settings')} className={tabBtn(tab === 'settings')} style={tab === 'settings' ? { backgroundColor: BRAND } : {}}>Settings</button>
          {isAdmin && <button onClick={() => setTab('admin')} className={tabBtn(tab === 'admin')} style={tab === 'admin' ? { backgroundColor: BRAND } : {}}>Admin</button>}
        </div>

        {tab === 'inbox'    && <Inbox T={T} onNavigate={onNavigate} />}
        {tab === 'settings' && <Settings T={T} />}
        {tab === 'admin' && isAdmin && <Admin T={T} />}
      </div>
    </div>
  );
}

// ── Inbox ─────────────────────────────────────────────────────────────────────
// A single feed row. On touch devices it supports horizontal swipe gestures:
// swipe RIGHT to mark read (orange, unread rows only), swipe LEFT to delete
// (red). Desktop is unaffected — mouse never fires touch events, and the tap
// targets (dot / body / trash) work exactly as before. `touchAction: 'pan-y'`
// lets vertical scrolling pass through untouched.
function NotificationRow({ n, T, onOpen, onMarkRead, onDelete }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef(null); // { x, y, axis: null|'h'|'v' }
  const canRead = !n.read_at;
  const COMMIT_READ = 60;   // swipe-right distance to mark read
  const COMMIT_DELETE = 96; // swipe-left distance to delete (more deliberate)
  const MAX = 112;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY, axis: null };
    setDragging(true);
  };
  const onTouchMove = (e) => {
    if (!start.current) return;
    const t = e.touches[0];
    let ddx = t.clientX - start.current.x;
    const ddy = t.clientY - start.current.y;
    if (!start.current.axis) {
      if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return;
      start.current.axis = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
    }
    if (start.current.axis !== 'h') return; // vertical gesture → let the page scroll
    if (ddx > 0 && !canRead) ddx = 0;       // nothing to do swiping an already-read row right
    setDx(Math.max(-MAX, Math.min(MAX, ddx)));
  };
  const onTouchEnd = (e) => {
    const axis = start.current?.axis;
    start.current = null;
    setDragging(false);
    if (axis === 'h') {
      if (e.cancelable) e.preventDefault(); // suppress the synthesized click after a swipe
      if (dx <= -COMMIT_DELETE) { setDx(-window.innerWidth); setTimeout(() => onDelete(n.id), 180); return; }
      if (dx >= COMMIT_READ && canRead) onMarkRead(n);
    }
    setDx(0);
  };

  return (
    <li className={`relative overflow-hidden border-b last:border-0 ${T.divider}`}>
      {/* Action layer revealed beneath the row as it slides. */}
      <div className="absolute inset-0 flex items-center justify-between px-5 text-white text-sm font-medium"
        style={{ backgroundColor: dx > 0 ? BRAND : dx < 0 ? '#dc2626' : 'transparent' }}>
        <span className={`flex items-center gap-1.5 ${dx > 0 && canRead ? '' : 'invisible'}`}>
          <Check size={16} /> Read
        </span>
        <span className={`flex items-center gap-1.5 ${dx < 0 ? '' : 'invisible'}`}>
          Delete <Trash2 size={16} />
        </span>
      </div>
      {/* Foreground (opaque so the action layer only shows once translated). */}
      <div
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        className={`relative flex items-stretch ${T.bg} ${T.row} transition`}
        style={{ transform: `translateX(${dx}px)`, transition: dragging ? 'none' : 'transform 0.18s ease', touchAction: 'pan-y' }}>
        {/* Unread dot doubles as a tap target: mark this one read without leaving the inbox. */}
        <button onClick={() => onMarkRead(n)} disabled={!!n.read_at}
          aria-label={n.read_at ? 'Read' : 'Mark as read'}
          title={n.read_at ? undefined : 'Mark as read'}
          className="shrink-0 pl-5 pr-2 py-3.5 flex items-start disabled:cursor-default">
          <span className="mt-0.5 block w-2.5 h-2.5 rounded-full transition"
            style={{ backgroundColor: n.read_at ? 'transparent' : BRAND }} />
        </button>
        <button onClick={() => onOpen(n)} className="min-w-0 flex-1 text-left pr-2 py-3.5 flex gap-3">
          <span className="min-w-0 flex-1">
            <span className="flex items-center justify-between gap-2">
              <span className={`font-medium text-sm truncate ${T.text} ${n.read_at ? 'opacity-60' : ''}`}>{n.title}</span>
              <span className={`text-xs shrink-0 ${T.sub}`}>{relTime(n.created_at)}</span>
            </span>
            {n.body && <span className={`block text-sm mt-0.5 ${T.sub}`}>{n.body}</span>}
            <span className={`inline-block mt-1 text-[11px] px-1.5 py-0.5 rounded ${T.chip}`}>{n.group}</span>
          </span>
        </button>
        <button onClick={() => onDelete(n.id)} aria-label="Delete notification"
          className={`shrink-0 px-3 flex items-center ${T.sub} hover:text-red-400 transition`}>
          <Trash2 size={16} />
        </button>
      </div>
    </li>
  );
}

function Inbox({ T, onNavigate }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  const load = () => fetchFeed(50).then((d) => { setItems(d.items); setAppBadge(d.unread || 0); }).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Optimistically flip the given ids to read + resync the app-icon badge. No
  // refetch — the list updates instantly (mirrors the delete flow), so marking
  // read never flashes or waits on the network.
  const applyRead = (ids) => {
    const set = new Set(ids);
    const now = new Date().toISOString();
    setItems((prev) => {
      const next = prev.map((i) => (set.has(i.id) && !i.read_at ? { ...i, read_at: now } : i));
      setAppBadge(next.filter((i) => !i.read_at).length);
      return next;
    });
  };

  // Mark a single notification read in place — no navigation. Backs the dot tap.
  const markOneRead = (n) => {
    if (n.read_at) return;
    applyRead([n.id]);
    markRead({ ids: [n.id] }).catch((e) => { toast.error(e.message); load(); });
  };

  // Open: mark read (if needed) and jump to the linked tool — deep-linking to the
  // specific item when the url carries a query (e.g. /incident-reports?id=45), which
  // becomes a `focus` prop the tool opens on. Rows that only link back to
  // /notifications just get marked read in place.
  const openItem = (n) => {
    if (!n.read_at) markOneRead(n);
    if (n.url && n.url !== '/notifications') {
      const { pageName, focus } = parseAppPath(n.url);
      if (pageName && pageName !== 'notifications') onNavigate(pageName, focus ? { focus } : {});
    }
  };

  const markAll = () => {
    const unreadIds = items.filter((i) => !i.read_at).map((i) => i.id);
    if (!unreadIds.length) return;
    applyRead(unreadIds);
    markRead({ all: true }).catch((e) => { toast.error(e.message); load(); });
  };

  const handleDelete = async (id) => {
    const prev = items;
    const next = items.filter((i) => i.id !== id);
    setItems(next);
    setAppBadge(next.filter((i) => !i.read_at).length);
    try { await deleteNotification(id); } catch (e) { toast.error(e.message); setItems(prev); load(); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all your notifications? This cannot be undone.')) return;
    const prev = items;
    setItems([]); setAppBadge(0);
    try { await clearNotifications(); } catch (e) { toast.error(e.message); setItems(prev); load(); }
  };

  const unreadCount = items.filter((i) => !i.read_at).length;
  const shown = filter === 'unread' ? items.filter((i) => !i.read_at) : items;

  if (loading) return <p className={`text-sm ${T.sub}`}>Loading…</p>;
  return (
    <div className={`rounded-xl border ${T.card}`}>
      <div className={`flex items-center justify-between gap-3 px-5 py-3 border-b ${T.divider} flex-wrap`}>
        <div className="flex items-center gap-1">
          {[['all', 'All'], ['unread', `Unread${unreadCount ? ` (${unreadCount})` : ''}`]].map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`text-xs font-medium px-2.5 py-1 rounded-md transition ${filter === v ? 'text-white' : `${T.chip} hover:opacity-80`}`}
              style={filter === v ? { backgroundColor: BRAND } : {}}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {unreadCount > 0 && (
            <button onClick={markAll} className={`text-xs flex items-center gap-1 ${T.sub} hover:opacity-80`}>
              <Check size={14} /> Mark all read
            </button>
          )}
          {items.length > 0 && (
            <button onClick={clearAll} className={`text-xs flex items-center gap-1 ${T.sub} hover:text-red-400 transition`}>
              <Trash2 size={14} /> Clear all
            </button>
          )}
        </div>
      </div>
      {!shown.length ? (
        <p className={`text-sm px-5 py-8 text-center ${T.sub}`}>
          {filter === 'unread' && items.length
            ? "You're all caught up — no unread notifications."
            : 'When something needs your attention, it’ll show up here.'}
        </p>
      ) : (
        <ul>
          {shown.map((n) => (
            <NotificationRow key={n.id} n={n} T={T}
              onOpen={openItem} onMarkRead={markOneRead} onDelete={handleDelete} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Settings (per-user channels + global controls) ─────────────────────────────
function Settings({ T }) {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPreferences().then((d) => { setTypes(d.types); setSettings(d.settings); }).catch((e) => toast.error(e.message));
    /* eslint-disable-next-line */
  }, []);

  const setChannel = (key, channel) => setTypes((ts) => ts.map((t) => (t.key === key ? { ...t, channel } : t)));
  const save = async () => {
    setSaving(true);
    try {
      const channels = Object.fromEntries(types.map((t) => [t.key, t.channel]));
      await savePreferences({ channels, settings });
      toast.success('Notification settings saved.');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!settings) return <p className={`text-sm ${T.sub}`}>Loading…</p>;

  // group types by their group label
  const groups = {};
  types.forEach((t) => { (groups[t.group] ||= []).push(t); });

  const selCls = `text-sm rounded px-2 py-1.5 ${T.input}`;
  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([group, list]) => (
        <div key={group} className={`rounded-xl border p-5 ${T.card}`}>
          <h3 className={`font-semibold text-lg mb-3 ${T.text}`}>{group}</h3>
          <div className="space-y-3">
            {list.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className={`text-sm font-medium ${T.text}`}>{t.label}</div>
                  <div className={`text-xs ${T.sub}`}>{t.description}</div>
                </div>
                <select value={t.channel} onChange={(e) => setChannel(t.key, e.target.value)} className={selCls}>
                  {t.allowedChannels.map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className={`rounded-xl border p-5 ${T.card}`}>
        <h3 className={`font-semibold text-lg ${T.text}`}>Quiet hours & limits</h3>
        <p className={`text-sm mt-0.5 mb-4 ${T.sub}`}>Push buzzes are held during quiet hours and capped per day — held ones roll into the daily digest.</p>
        <div className="grid grid-cols-2 gap-4">
          <label className={`text-sm ${T.sub}`}>Quiet from
            <select value={settings.quiet_start_hour ?? ''} onChange={(e) => setSettings((s) => ({ ...s, quiet_start_hour: e.target.value === '' ? null : +e.target.value }))} className={`mt-1 block w-full ${selCls}`}>
              <option value="">—</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </label>
          <label className={`text-sm ${T.sub}`}>Quiet until
            <select value={settings.quiet_end_hour ?? ''} onChange={(e) => setSettings((s) => ({ ...s, quiet_end_hour: e.target.value === '' ? null : +e.target.value }))} className={`mt-1 block w-full ${selCls}`}>
              <option value="">—</option>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </label>
          <label className={`text-sm ${T.sub}`}>Max pushes / 24h
            <input type="number" min="1" max="20" value={settings.daily_push_cap} onChange={(e) => setSettings((s) => ({ ...s, daily_push_cap: +e.target.value }))} className={`mt-1 block w-full ${selCls}`} />
          </label>
          <label className={`text-sm ${T.sub}`}>Daily digest at
            <select value={settings.digest_hour} onChange={(e) => setSettings((s) => ({ ...s, digest_hour: +e.target.value }))} className={`mt-1 block w-full ${selCls}`}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </label>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="px-4 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-60" style={{ backgroundColor: BRAND }}>
        {saving ? 'Saving…' : 'Save settings'}
      </button>
    </div>
  );
}

// ── Admin (catalog kill-switch + broadcast + digest) ───────────────────────────
function Admin({ T }) {
  const toast = useToast();
  const [catalog, setCatalog] = useState([]);
  const [log, setLog] = useState([]);
  const [bc, setBc] = useState({ target: 'all', roles: [], title: '', body: '' });
  const [busy, setBusy] = useState(false);

  const load = () => getCatalog().then((d) => { setCatalog(d.catalog); setLog(d.log); }).catch((e) => toast.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (key, enabled) => {
    setCatalog((c) => c.map((t) => (t.key === key ? { ...t, orgEnabled: enabled } : t)));
    try { await setTypeEnabled(key, enabled); } catch (e) { toast.error(e.message); load(); }
  };
  const toggleRole = (r) => setBc((b) => ({ ...b, roles: b.roles.includes(r) ? b.roles.filter((x) => x !== r) : [...b.roles, r] }));
  const send = async () => {
    if (!bc.title.trim()) return toast.error('Enter a title.');
    setBusy(true);
    try {
      const res = await broadcast({ all: bc.target === 'all', roles: bc.target === 'roles' ? bc.roles : undefined, title: bc.title, body: bc.body });
      toast.success(`Sent to ${res.recipients} ${res.recipients === 1 ? 'person' : 'people'}.`);
      setBc({ target: 'all', roles: [], title: '', body: '' }); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const flush = async () => { try { const r = await flushDigestsNow(); toast.success(`Digest flushed for ${r.users} ${r.users === 1 ? 'user' : 'users'}.`); load(); } catch (e) { toast.error(e.message); } };

  const selCls = `text-sm rounded px-2 py-1.5 ${T.input}`;
  return (
    <div className="space-y-5">
      {/* Catalog */}
      <div className={`rounded-xl border p-5 ${T.card}`}>
        <h3 className={`font-semibold text-lg mb-1 ${T.text}`}>Notification types</h3>
        <p className={`text-sm mb-4 ${T.sub}`}>The org-wide kill-switch. Turning a type off mutes it for everyone, no deploy needed.</p>
        <div className="space-y-2.5">
          {catalog.map((t) => (
            <div key={t.key} className={`flex items-center justify-between gap-3 py-1.5 border-b last:border-0 ${T.divider}`}>
              <div className="min-w-0">
                <div className={`text-sm font-medium ${T.text}`}>{t.label} <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${T.chip}`}>{t.group}</span></div>
                <div className={`text-xs ${T.sub}`}>{t.description}</div>
                <div className={`text-[11px] mt-0.5 ${T.sub}`}>default: {CHANNEL_LABELS[t.defaultChannel]}{t.roles.length ? ` · ${t.roles.join(', ')}` : ' · everyone'}</div>
              </div>
              <button onClick={() => toggle(t.key, !t.orgEnabled)} className={`shrink-0 px-2.5 py-1 rounded text-xs font-medium ${t.orgEnabled ? 'text-white' : T.chip}`} style={t.orgEnabled ? { backgroundColor: BRAND } : {}}>
                {t.orgEnabled ? 'On' : 'Off'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Broadcast */}
      <div className={`rounded-xl border p-5 ${T.card}`}>
        <h3 className={`font-semibold text-lg mb-1 ${T.text}`}>Send an announcement</h3>
        <p className={`text-sm mb-4 ${T.sub}`}>Goes through the engine as an “Announcement,” so it respects each person’s channel choice.</p>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setBc((b) => ({ ...b, target: 'all' }))} className={`px-3 py-1.5 rounded text-sm ${bc.target === 'all' ? 'text-white' : T.chip}`} style={bc.target === 'all' ? { backgroundColor: BRAND } : {}}>Everyone</button>
          <button onClick={() => setBc((b) => ({ ...b, target: 'roles' }))} className={`px-3 py-1.5 rounded text-sm ${bc.target === 'roles' ? 'text-white' : T.chip}`} style={bc.target === 'roles' ? { backgroundColor: BRAND } : {}}>By role</button>
        </div>
        {bc.target === 'roles' && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {ROLES.map((r) => (
              <button key={r} onClick={() => toggleRole(r)} className={`px-2 py-1 rounded text-xs ${bc.roles.includes(r) ? 'text-white' : T.chip}`} style={bc.roles.includes(r) ? { backgroundColor: BRAND } : {}}>{r}</button>
            ))}
          </div>
        )}
        <input value={bc.title} onChange={(e) => setBc((b) => ({ ...b, title: e.target.value }))} placeholder="Title" className={`w-full mb-2 ${selCls}`} />
        <textarea value={bc.body} onChange={(e) => setBc((b) => ({ ...b, body: e.target.value }))} placeholder="Message (optional)" rows={2} className={`w-full mb-3 ${selCls}`} />
        <button onClick={send} disabled={busy} className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-60" style={{ backgroundColor: BRAND }}>
          <Send size={15} /> {busy ? 'Sending…' : 'Send'}
        </button>
      </div>

      {/* Log + digest */}
      <div className={`rounded-xl border p-5 ${T.card}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold text-lg ${T.text}`}>Recent activity</h3>
          <button onClick={flush} className={`text-xs flex items-center gap-1 ${T.sub} hover:opacity-80`}><RefreshCw size={13} /> Flush digests now</button>
        </div>
        {!log.length ? <p className={`text-sm ${T.sub}`}>No notifications sent yet.</p> : (
          <div className="space-y-1.5">
            {log.map((l) => (
              <div key={l.id} className={`text-xs flex items-center justify-between gap-2 ${T.sub}`}>
                <span className="truncate"><span className={T.text}>{l.user_name}</span> · {l.title}</span>
                <span className="shrink-0">{l.push_sent_at ? 'pushed' : l.digest_pending ? 'queued' : l.channel} · {relTime(l.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
